/* eslint-disable @typescript-eslint/naming-convention */
import * as React from 'react'
import { EditorState, ContentState, convertToRaw } from 'draft-js'
import { Editor } from 'react-draft-wysiwyg'
// eslint-disable-next-line import/no-internal-modules
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import './BodyEditor.css'
import htmlToDraft from 'html-to-draftjs'
import draftToHtml from 'draftjs-to-html'
import DOMPurify from 'dompurify'

interface BodyEditorProps {
	name: string
	setBody: (body: string) => void
	toggleEditable: (name?: string) => void
	html?: string
	placeholder?: string
	editable?: boolean
	imageUploadCallback: (e: any) => Promise<{ data: { link: string } }>
}

const toolbarOptions = [
	'inline',
	'blockType',
	'fontSize',
	'fontFamily',
	'list',
	'textAlign',
	'colorPicker',
	'link',
	'image',
	'history',
	'embedded',
]
const imageOptions = {
	popupClassName: 'image-upload-popup',
	urlEnabled: true,
	uploadEnabled: true,
	alignmentEnabled: true,
	inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
	alt: { present: false, mandatory: false },
	defaultSize: {
		height: 'auto',
		width: '100%',
	},
}

const linkOptions = {
	defaultTargetOption: '_blank',
	options: ['link', 'unlink'],
}

const embeddedOptions = {
	popupClassName: 'embedded-popup',
	defaultSize: {
		height: '360',
		width: '100%',
	},
	embedCallback: (url: string) => {
		const srcSplit = url.split('src="')
		if (srcSplit.length > 1) {
			const srcVal = srcSplit[1].split('"')[0]
			return srcVal
		}
		return url
	},
}

const createStateFromHtml = (html: string) => {
	const blocksFromHtml = htmlToDraft(html)
	const { contentBlocks, entityMap } = blocksFromHtml
	const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap)

	return EditorState.createWithContent(contentState)
}

const createHtmlFromState = (editorState: EditorState) => {
	const rawContentState = convertToRaw(editorState.getCurrentContent())

	return draftToHtml(rawContentState)
}

const htmlSanitizeSettings = {
	ADD_TAGS: ['iframe'],
	ADD_ATTR: ['allowfullscreen'],
}

const BodyEditor = (props: BodyEditorProps) => {
	DOMPurify.setConfig(htmlSanitizeSettings)

	const initialState = props.html && props.html !== '' ? createStateFromHtml(props.html) : EditorState.createEmpty()
	const [editorState, setEditorState] = React.useState(initialState)

	const setEditorHtml = () => {
		const html = createHtmlFromState(editorState)
		const htmlWithIframeSettings = addPropertiesToIframes(html)
		const purifiedHtml = DOMPurify.sanitize(htmlWithIframeSettings)
		props.setBody(purifiedHtml)
	}

	/*
	 * Adds a div element around iframes and adds class="embedded-video" around so we can use CSS to make iframes reponsive
	 */
	const addPropertiesToIframes = (html: string) => {
		return (
			html
				// Let's assume embedded iframes are videos
				.replace(/<iframe/g, '<div class="iframe-container"><iframe class="embedded-video"')
				.replace(/<\/iframe>/g, '</iframe></div>')
				.replace(/iframe width=/g, 'iframe allowfullscreen width=')
				// Let's remove embedded-video class from embedded spotify iframes
				// This should be done for all non-video embeds because otherwise the iframe are made to have 16:9 aspect ratio
				.replace(
					// eslint-disable-next-line no-useless-escape
					/class=\"embedded-video\" src=\"https:\/\/open\.spotify\.com/g,
					'src="https://open.spotify.com',
				)
		)
	}

	const editorStyle = {
		minHeight: '150px',
		paddingLeft: '5px',
		paddingRight: '5px',
	}

	return props.editable ? (
		<Editor
			editorStyle={editorStyle}
			editorState={editorState}
			onEditorStateChange={setEditorState}
			onBlur={setEditorHtml}
			toolbar={{
				options: toolbarOptions,
				image: {
					...imageOptions,
					uploadCallback: props.imageUploadCallback,
				},
				link: {
					...linkOptions,
				},
				embedded: {
					...embeddedOptions,
				},
			}}
			placeholder={props.placeholder}
		/>
	) : (
		<div
			onClick={() => props.toggleEditable(props.name)}
			dangerouslySetInnerHTML={
				props.html
					? { __html: DOMPurify.sanitize(props.html!) }
					: { __html: DOMPurify.sanitize(props.placeholder!) }
			}
		/>
	)
}

export default BodyEditor
